/**
 * HookKeys — named event hooks that game systems fire into the ModifierBag.
 *
 * Skills, perks, items, and buffs can register callbacks against these hooks
 * to implement conditional or event-driven effects.
 *
 * Each hook documents its callback signature so sources know what args to expect.
 */

/**
 * Fired when a valid word is scored, before damage is dealt.
 * Callback: (ctx: { word: string, wordScore: number, damage: number }) => { damage?: number }
 * Returning { damage } overrides the damage value.
 */
export const ON_WORD_SCORED = "onWordScored";

/**
 * Fired at the start of each player turn.
 * Callback: (ctx: { turnNumber: number }) => void
 */
export const ON_TURN_START = "onTurnStart";

/**
 * Fired at the start of a battle.
 * Callback: () => void
 */
export const ON_BATTLE_START = "onBattleStart";

/**
 * Fired when an enemy is killed.
 * Callback: (ctx: { enemy: EnemyCharacter, index: number }) => void
 */
export const ON_ENEMY_KILLED = "onEnemyKilled";

/**
 * Fired when the player takes damage (after armor).
 * Callback: (ctx: { rawDamage: number, actualDamage: number }) => void
 */
export const ON_DAMAGE_TAKEN = "onDamageTaken";

/**
 * Fired when the player heals.
 * Callback: (ctx: { amount: number }) => void
 */
export const ON_HEAL = "onHeal";

/**
 * Fired when the battle is won.
 * Callback: (ctx: { gold: number, xp: number }) => { gold?: number, xp?: number }
 * Returning overrides lets hooks modify rewards.
 */
export const ON_VICTORY = "onVictory";

/**
 * Fired when a word is submitted (valid or not), before validation.
 * Callback: (ctx: { word: string }) => void
 */
export const ON_WORD_SUBMITTED = "onWordSubmitted";
